import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle, Building2, Save, Plus, Sparkles } from 'lucide-react';
import { getModuleEntity } from '../../config/moduleHelpers';

const AddCompanyModal = ({ onClose, onSave, config }) => {
    const entity = getModuleEntity(config);
    const mainField = config?.fieldMappings?.mainField || 'company_name';
    const numberField = config?.fieldMappings?.numberField || 'company_number';
    const addFields = config?.addModalFields?.length
        ? config.addModalFields
        : [
            { key: numberField, label: entity.numberLabel, required: true },
            { key: mainField, label: `${entity.singular} Name`, required: true }
        ];
    const onboardingPresets = config?.onboardingPresets || [];

    const resolveDefaults = () => {
        const defaults = config?.newRecordDefaults || {};
        return Object.fromEntries(
            Object.entries(defaults).map(([key, value]) => [
                key,
                typeof value === 'function' ? value() : value
            ])
        );
    };

    const getPresetValues = (presetId) =>
        onboardingPresets.find(preset => preset.id === presetId)?.values || {};

    const createEmptyRow = (presetId = '') => ({
        ...resolveDefaults(),
        ...getPresetValues(presetId),
        ...Object.fromEntries(addFields.map(field => [field.key, '']))
    });

    const [selectedPresetId, setSelectedPresetId] = useState('');

    const [rows, setRows] = useState([
        createEmptyRow(selectedPresetId),
        createEmptyRow(selectedPresetId),
        createEmptyRow(selectedPresetId)
    ]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const handleRowChange = (index, field, value) => {
        const updated = [...rows];
        updated[index][field] = value;
        setRows(updated);
    };

    const addMoreRows = () => {
        setRows([...rows, createEmptyRow(selectedPresetId)]);
    };

    const handlePresetChange = (presetId) => {
        setSelectedPresetId(presetId);
        const presetValues = getPresetValues(presetId);
        const defaults = resolveDefaults();

        setRows(currentRows =>
            currentRows.map(row => {
                const identityValues = Object.fromEntries(
                    addFields.map(field => [field.key, row[field.key] || ''])
                );
                return {
                    ...row,
                    ...defaults,
                    ...presetValues,
                    ...identityValues
                };
            })
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Filter out empty rows
        const requiredFields = addFields.filter(field => field.required);
        const validRows = rows.filter(row =>
            requiredFields.every(field => String(row[field.key] || '').trim())
        );

        if (validRows.length === 0) {
            const requiredFieldLabels = requiredFields.map(field => field.label.toLowerCase()).join(' and ');
            setError(`Please enter at least one ${entity.singular.toLowerCase()} with ${requiredFieldLabels}.`);
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            await onSave(validRows);
            setIsSaving(false);
            onClose();
        } catch (err) {
            console.error('Save error:', err);
            setError(err.message || `Failed to add ${entity.plural.toLowerCase()}. Please try again.`);
            setIsSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-indigo-600">
                    <div>
                        <h3 className="text-lg font-bold text-white flex items-center gap-3">
                            <Building2 className="w-6 h-6" />
                            {entity.addLabel}
                        </h3>
                        <p className="text-xs text-blue-100 mt-0.5">Enter {entity.singular.toLowerCase()} details to add to the system</p>
                    </div>
                    <button onClick={onClose} className="text-blue-100 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors">
                        <XCircle className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-4">
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                {error}
                            </div>
                        )}

                        {onboardingPresets.length > 0 && (
                            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl space-y-2">
                                <label className="text-xs font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" />
                                    Onboarding Preset
                                </label>
                                <select
                                    value={selectedPresetId}
                                    onChange={e => handlePresetChange(e.target.value)}
                                    className="w-full px-3 py-2 bg-white border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm text-slate-900"
                                >
                                    <option value="">Custom (No Preset)</option>
                                    {onboardingPresets.map(preset => (
                                        <option key={preset.id} value={preset.id}>{preset.label}</option>
                                    ))}
                                </select>
                                {selectedPresetId && (
                                    <p className="text-xs text-indigo-800">
                                        {onboardingPresets.find(preset => preset.id === selectedPresetId)?.description}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="space-y-3">
                            <div className={`grid gap-3 items-center`} style={{ gridTemplateColumns: `60px repeat(${addFields.length}, minmax(0, 1fr))` }}>
                                <div></div>
                                {addFields.map(field => (
                                    <label key={field.key} className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                ))}
                            </div>

                            <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
                                {rows.map((row, index) => (
                                    <div key={index} className="grid gap-3 items-center" style={{ gridTemplateColumns: `60px repeat(${addFields.length}, minmax(0, 1fr))` }}>
                                        <div className="text-sm font-semibold text-slate-600 text-center">{index + 1}</div>
                                        {addFields.map(field => (
                                            <input
                                                key={field.key}
                                                type="text"
                                                className={`px-3 py-2 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-900 placeholder-slate-400 transition-all ${field.inputClassName || ''}`.trim()}
                                                placeholder={field.placeholder || ''}
                                                value={row[field.key] || ''}
                                                onChange={e => handleRowChange(index, field.key, e.target.value)}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-center pt-2">
                                <button
                                    type="button"
                                    onClick={addMoreRows}
                                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-all"
                                >
                                    <Plus className="w-4 h-4" />
                                    &lt;&lt;add more lines&gt;&gt;
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-t border-slate-200 flex justify-end gap-3 shadow-sm">
                        <button 
                            type="button"
                            onClick={onClose} 
                            className="px-6 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit"
                            disabled={isSaving} 
                            className="flex items-center gap-2 px-8 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-md transition-all"
                        >
                            {isSaving ? 'Saving...' : <><Save className="w-4 h-4" /> {entity.addLabel}</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default AddCompanyModal;
